import React, { useState, useEffect, useRef } from 'react';
import { PullRequest, ReviewReport, FeedbackLoopStep, ReviewComment } from '../types';
import {
  GitPullRequest,
  Terminal as TerminalIcon,
  CheckCircle2,
  Clock,
  ArrowLeft,
  FileCode,
  MessageSquare,
  Sparkles,
  Check,
  Send,
  X,
  AlertTriangle
} from 'lucide-react';
import { DiffViewer } from './DiffViewer';
import { Markdown } from './Markdown';

interface PrDetailsProps {
  prId: string;
  onBack: () => void;
  onTriggerReview: (prId: string) => void;
  onInteractComment: (prId: string, commentId: string, action: 'accept' | 'refine' | 'dismiss' | 'apply', feedback?: string) => Promise<{ message: string; comment?: ReviewComment }>;
  pr: PullRequest | null;
  report: ReviewReport | null;
  loading: boolean;
}

export function PrDetails({ prId, onBack, onTriggerReview, onInteractComment, pr, report, loading }: PrDetailsProps) {
  const [activeTab, setActiveTab] = useState<'loops' | 'code' | 'comments' | 'raw_report'>('loops');
  const [refineInputs, setRefineInputs] = useState<Record<string, string>>({});
  const [localFeedbackSteps, setLocalFeedbackSteps] = useState<FeedbackLoopStep[]>([]);
  const [localReport, setLocalReport] = useState<ReviewReport | null>(null);
  const [pendingCommentId, setPendingCommentId] = useState<string | null>(null);
  const terminalEndRef = useRef<HTMLDivElement>(null);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // When a comment pin is clicked in the Code tab, we switch to the comments
  // tab and set pendingCommentId. This effect runs AFTER React commits that tab
  // (so the comment card is in the DOM), then scrolls to it and flashes it.
  // A single requestAnimationFrame after setActiveTab fires too early — the
  // comments panel isn't mounted yet — so the lookup silently missed.
  useEffect(() => {
    if (activeTab !== 'comments' || !pendingCommentId) return;
    const el = document.getElementById(`comment-card-${pendingCommentId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-1', 'ring-inset', 'ring-amber-300');
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      flashTimerRef.current = setTimeout(() => el.classList.remove('ring-1', 'ring-inset', 'ring-amber-300'), 1800);
    }
    setPendingCommentId(null);
  }, [activeTab, pendingCommentId]);

  // Cancel a pending flash timer if the component unmounts mid-flash.
  useEffect(() => () => { if (flashTimerRef.current) clearTimeout(flashTimerRef.current); }, []);

  useEffect(() => {
    if (report?.feedbackLoops) {
      setLocalFeedbackSteps(report.feedbackLoops);
    }
    // Sync the comments list when the report prop changes (fresh fetch or refetch),
    // so a reopened drawer doesn't show stale local patches.
    setLocalReport(report);
  }, [report]);

  useEffect(() => {
    if (terminalEndRef.current) {
      terminalEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [localFeedbackSteps, loading]);

  if (!pr) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-[#91918E] bg-white border border-[#EDEDEB] rounded-xl" id="pr-not-found">
        <GitPullRequest className="w-12 h-12 text-[#91918E] animate-bounce mb-4" />
        <p className="text-sm font-sans">Retrieving pull request details from secure cache...</p>
      </div>
    );
  }

  // Patch the server-returned comment into local state so the card repaints
  // immediately without waiting for a report refetch (dismiss/apply/accept).
  const handleInteract = async (commentId: string, action: 'accept' | 'dismiss' | 'apply' | 'refine', feedback?: string) => {
    const res = await onInteractComment(pr.id, commentId, action, feedback);
    if (res?.comment) {
      setLocalReport(prev => prev
        ? { ...prev, comments: prev.comments.map(c => c.id === res.comment!.id ? res.comment! : c) }
        : prev);
    }
  };

  // Comments tab renders from liveReport (local patches + prop), so interactions
  // repaint immediately. Raw Report panel keeps the server `report` as-is.
  const liveReport = localReport ?? report;

  const handleRefineSubmit = async (commentId: string) => {
    const text = refineInputs[commentId];
    if (!text?.trim()) return;

    // Clear the input
    setRefineInputs(prev => ({ ...prev, [commentId]: '' }));

    // Call interaction endpoint
    await handleInteract(commentId, 'refine', text);
  };

  return (
    <div className="space-y-8" id="pr-detail-layout">
      
      {/* Back button & Action Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[#EDEDEB] pb-6" id="pr-detail-header">
        <button 
          onClick={onBack}
          className="flex items-center gap-2 text-[#91918E] hover:text-[#191919] text-xs font-mono transition duration-100"
          id="btn-back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Pull Requests
        </button>

        {pr.status === 'open' && (
          <button
            onClick={() => onTriggerReview(pr.id)}
            disabled={loading}
            className="flex items-center justify-center gap-2 bg-[#191919] hover:bg-[#2A2A28] disabled:opacity-50 text-white font-sans text-xs font-semibold px-4 py-2.5 rounded-lg shadow-sm transition duration-100"
            id="btn-trigger-review"
          >
            <TerminalIcon className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Executing Agentic Review Loop...' : 'Launch Nim Agent Review'}
          </button>
        )}
      </div>

      {/* PR Identity Summary Card */}
      <div className="bg-[#F7F7F5] border border-[#EDEDEB] rounded-xl p-6" id="pr-meta-card">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="font-mono text-[10px] text-[#205D7C] font-semibold uppercase bg-[#D3E5EF] px-2 py-0.5 rounded">
                PR #{pr.prNumber ?? pr.id.replace(/^pr-/, '')}
              </span>
              <h1 className="text-xl font-bold text-[#191919]">{pr.title}</h1>
            </div>

            {pr.description?.trim() ? (
              <div className="text-xs max-w-3xl text-[#91918E]">
                <Markdown>{pr.description}</Markdown>
              </div>
            ) : null}

            <div className="flex items-center gap-3 pt-1 text-[11px] text-[#91918E] font-sans">
              <img 
                src={pr.authorAvatar} 
                alt={pr.author} 
                className="w-5 h-5 rounded-full border border-[#EDEDEB] object-cover" 
                referrerPolicy="no-referrer"
              />
              <span className="text-[#191919] font-medium">{pr.author}</span>
              <span className="text-[#EDEDEB]">|</span>
              <span className="font-mono text-[10px]">branch:</span>
              <span className="bg-white text-[#191919] px-1.5 py-0.5 rounded text-[10px] border border-[#EDEDEB] font-mono">
                {pr.branch}
              </span>
              <span>→</span>
              <span className="text-[#191919] font-medium">{pr.targetBranch}</span>
            </div>
          </div>

          <div className="flex flex-col items-end gap-2 shrink-0">
            {pr.status === 'open' ? (
              <span className="inline-flex items-center gap-1.5 bg-[#E3F2FD] border border-[#B3E5FC] text-[#0D47A1] text-xs px-3 py-1 rounded-full font-sans font-medium">
                <Clock className="w-3.5 h-3.5" />
                Queue: Pending Review
              </span>
            ) : (
              <div className="text-right space-y-1">
                <span className="inline-flex items-center gap-1.5 bg-[#E8F5E9] border border-[#C8E6C9] text-[#2E7D32] text-xs px-3 py-1 rounded-full font-sans font-medium">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Code Reviewed
                </span>
                {pr.reviewStatus && (
                  <p className="text-[10px] text-[#91918E] font-mono">
                    Score: {pr.reviewStatus.score}/100 • Conclusion: {pr.reviewStatus.conclusion.toUpperCase()}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex border-b border-[#EDEDEB]" id="pr-detail-tabs">
        <button
          onClick={() => setActiveTab('loops')}
          className={`flex items-center gap-2 px-5 py-3 text-xs font-semibold uppercase tracking-wider border-b-2 transition duration-100 ${
            activeTab === 'loops' 
              ? 'border-[#191919] text-[#191919] bg-[#F7F7F5]' 
              : 'border-transparent text-[#91918E] hover:text-[#191919]'
          }`}
          id="tab-loops"
        >
          <TerminalIcon className="w-4 h-4" />
          Nim CLI Loops
        </button>
        <button
          onClick={() => setActiveTab('code')}
          className={`flex items-center gap-2 px-5 py-3 text-xs font-semibold uppercase tracking-wider border-b-2 transition duration-100 ${
            activeTab === 'code' 
              ? 'border-[#191919] text-[#191919] bg-[#F7F7F5]' 
              : 'border-transparent text-[#91918E] hover:text-[#191919]'
          }`}
          id="tab-code"
        >
          <FileCode className="w-4 h-4" />
          Code Diff
        </button>
        <button
          onClick={() => setActiveTab('comments')}
          className={`flex items-center gap-2 px-5 py-3 text-xs font-semibold uppercase tracking-wider border-b-2 transition duration-100 ${
            activeTab === 'comments' 
              ? 'border-[#191919] text-[#191919] bg-[#F7F7F5]' 
              : 'border-transparent text-[#91918E] hover:text-[#191919]'
          }`}
          id="tab-comments"
        >
          <MessageSquare className="w-4 h-4" />
          Interactive Suggestions ({liveReport?.comments.length || 0})
        </button>
        <button
          onClick={() => setActiveTab('raw_report')}
          className={`flex items-center gap-2 px-5 py-3 text-xs font-semibold uppercase tracking-wider border-b-2 transition duration-100 ${
            activeTab === 'raw_report' 
              ? 'border-[#191919] text-[#191919] bg-[#F7F7F5]' 
              : 'border-transparent text-[#91918E] hover:text-[#191919]'
          }`}
          id="tab-raw-report"
        >
          <FileCode className="w-4 h-4 text-emerald-600" />
          Raw Report
        </button>
      </div>

      {/* Tab Panels */}
      <div className="space-y-6" id="tab-panel-container">
        
        {/* LOOP PANEL: Detailed Interactive Nim Terminal */}
        {activeTab === 'loops' && (
          <div className="space-y-6" id="panel-loops">
            {/* Terminal Window with modern contrast styling */}
            <div className="bg-[#191919] border border-[#2D2D2A] rounded-xl overflow-hidden shadow-xl" id="claude-terminal-cli">
              
              {/* Terminal Window Header */}
              <div className="bg-[#242422] px-4 py-3 border-b border-[#2D2D2A] flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#E0544E] block"></span>
                  <span className="w-2.5 h-2.5 rounded-full bg-[#E5AA3C] block"></span>
                  <span className="w-2.5 h-2.5 rounded-full bg-[#52C04B] block"></span>
                  <span className="text-[10px] font-mono text-[#91918E] ml-2">nim@cli:~</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[9px] font-mono bg-[#191919] text-[#91918E] px-2 py-0.5 rounded border border-[#2D2D2A]">
                    bash (v0.2.14)
                  </span>
                </div>
              </div>

              {/* Terminal Logs Area */}
              <div className="p-5 font-mono text-xs space-y-4 max-h-[500px] overflow-y-auto leading-relaxed text-[#F3F2F1]" id="terminal-screen">
                
                {/* Initial Instruction line */}
                <div className="text-[#91918E]" id="term-intro">
                  {`# Nim Automated Feedback Loops started. Ready to execute code-review audit on ${pr.branch}.`}
                </div>

                {localFeedbackSteps.length === 0 && !loading && (
                  <div className="text-[#91918E] py-16 text-center flex flex-col items-center justify-center gap-3">
                    <TerminalIcon className="w-8 h-8 text-[#91918E]/30 animate-pulse" />
                    <p className="font-mono text-xs">Terminal is idle. Click "Launch Nim Agent Review" to run the automated audit loops.</p>
                  </div>
                )}

                {/* Print individual steps */}
                {localFeedbackSteps.map((step) => (
                  <div key={step.id} className="space-y-2 border-b border-white/5 pb-3.5" id={`term-step-${step.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-[#91918E]">$</span>
                        <span className="text-indigo-300 font-bold">{step.command}</span>
                      </div>
                      <span className="text-[9px] text-[#91918E]">
                        {new Date(step.timestamp).toLocaleTimeString()}
                      </span>
                    </div>

                    <pre className="bg-[#242422] p-3.5 rounded-lg text-slate-100 border border-white/5 whitespace-pre-wrap font-mono text-xs leading-5">
                      {step.output}
                    </pre>

                    <div className="flex items-center gap-2 text-[10px] font-mono text-[#91918E]">
                      <span>Status:</span>
                      {step.status === 'success' ? (
                        <span className="text-[#52C04B] font-semibold flex items-center gap-1">
                          <Check className="w-3 h-3" /> success
                        </span>
                      ) : step.status === 'failure' ? (
                        <span className="text-[#E0544E] font-semibold flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> failed / error loop
                        </span>
                      ) : (
                        <span className="text-amber-300 font-semibold flex items-center gap-1">
                          <span className="w-2 h-2 rounded-full bg-amber-400 animate-ping inline-block mr-1" /> running
                        </span>
                      )}
                      
                      {step.cycle && (
                        <span className="text-[#91918E] ml-2">Iteration Loop: Cycle #{step.cycle}</span>
                      )}
                    </div>
                  </div>
                ))}

                {/* Loading Stream effect */}
                {loading && (
                  <div className="space-y-3" id="terminal-loading-spinner">
                    <div className="flex items-center gap-2 text-indigo-300 font-semibold">
                      <span className="text-[#91918E]">$</span>
                      <span className="animate-pulse">nim review --agentic-mode --test-retry=3</span>
                    </div>
                    <div className="bg-[#242422] p-4 rounded-lg border border-white/5 flex items-center gap-3">
                      <span className="w-2 h-2 rounded-full bg-indigo-400 animate-ping" />
                      <span className="text-[#91918E] text-xs animate-pulse font-mono leading-normal">
                        Nim CLI is reading changed files, running Virtual Test Suite, identifying failure assertions, and drafting self-correcting patches...
                      </span>
                    </div>
                  </div>
                )}

                <div ref={terminalEndRef} />
              </div>
            </div>

            {/* Loop Summary explaining the automation */}
            {pr.status === 'reviewed' && (
              <div className="bg-[#F7F7F5] border border-[#EDEDEB] rounded-xl p-6 space-y-4" id="loop-explanation">
                <h3 className="font-sans font-bold text-[#191919] text-sm flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#191919]" />
                  What did the Nim feedback loop do?
                </h3>
                <p className="text-xs text-[#191919] leading-relaxed font-sans">
                  Nim doesn't just read code; it simulates an active engineer in terminal. On this pull request:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-xs font-mono pt-2">
                  <div className="bg-white p-4.5 border border-[#EDEDEB] rounded-lg space-y-1.5">
                    <div className="text-indigo-600 font-bold uppercase tracking-wide text-[11px]">1. AUDITED & TESTED</div>
                    <p className="text-[#91918E] font-sans text-[11px] leading-relaxed">
                      Scanned files, searched for associated dependencies, and executed test suites. Identified a critical code defect causing unit tests to crash.
                    </p>
                  </div>
                  <div className="bg-white p-4.5 border border-[#EDEDEB] rounded-lg space-y-1.5">
                    <div className="text-amber-600 font-bold uppercase tracking-wide text-[11px]">2. SELF-CORRECTED PATCH</div>
                    <p className="text-[#91918E] font-sans text-[11px] leading-relaxed">
                      Passed failure logs back into the LLM core context, formulated a precise surgical patch, applied the fix, and verified test passes with 100% resolution.
                    </p>
                  </div>
                  <div className="bg-white p-4.5 border border-[#EDEDEB] rounded-lg space-y-1.5">
                    <div className="text-emerald-600 font-bold uppercase tracking-wide text-[11px]">3. COMMITTED & COMMENTED</div>
                    <p className="text-[#91918E] font-sans text-[11px] leading-relaxed">
                      Pushed the revised green code back to the target branch, and populated the PR review thread with inline suggestions for interactive review.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* CODE TAB: real diff viewer */}
        {activeTab === 'code' && (
          <DiffViewer
            files={pr.files}
            comments={report?.comments}
            onCommentClick={(commentId) => {
              // Defer the scroll/flash to the post-commit effect so the comments
              // card is mounted before we look it up.
              setPendingCommentId(commentId);
              setActiveTab('comments');
            }}
          />
        )}

        {/* COMMENTS TAB: Dynamic Inline Suggestions and refined loop triggers */}
        {activeTab === 'comments' && (
          <div className="space-y-6" id="panel-comments">
            {(!liveReport || liveReport.comments.length === 0) ? (
              <div className="flex flex-col items-center justify-center p-12 text-[#91918E] bg-[#F7F7F5] border border-[#EDEDEB] rounded-xl" id="comments-empty">
                <MessageSquare className="w-10 h-10 text-[#91918E]/40 mb-2" />
                <p className="text-xs font-mono">No review comments yet. Trigger Nim review to produce comments.</p>
              </div>
            ) : (
              <div className="space-y-6" id="comments-feed">
                {/* Overall Summary Card */}
                <div className="bg-[#F7F7F5] border border-[#EDEDEB] rounded-xl p-6 space-y-3" id="overall-summary-card">
                  <h3 className="text-xs font-bold text-[#191919] flex items-center gap-2 font-mono uppercase tracking-wider">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    Overall Review Summary
                  </h3>
                  <div className="text-xs text-[#191919]">
                    <Markdown>{liveReport.overallSummary}</Markdown>
                  </div>
                </div>

                {/* Interactive Inline Comments list */}
                {liveReport.comments.map((comment) => (
                  <div key={comment.id} className="bg-white border border-[#EDEDEB] rounded-xl overflow-hidden shadow-xs" id={`comment-card-${comment.id}`}>
                    
                    {/* Comment author bar */}
                    <div className="bg-[#F7F7F5] p-4 border-b border-[#EDEDEB] flex justify-between items-center text-xs font-mono">
                      <div className="flex items-center gap-2 text-[#191919]">
                        <span className="bg-[#D3E5EF] text-[#205D7C] px-2 py-0.5 rounded font-bold text-[10px] tracking-wide">
                          SUGGESTION
                        </span>
                        <span className="font-semibold">{comment.filepath}</span>
                        <span className="text-[#91918E]">•</span>
                        <span className="text-[#91918E]">Line {comment.line}</span>
                      </div>
                      
                      {comment.status === 'resolved' ? (
                        <span className="text-[#2E7D32] bg-[#E8F5E9] px-2.5 py-0.5 rounded font-bold text-[10px] flex items-center gap-1 font-mono">
                          <Check className="w-3.5 h-3.5" /> RESOLVED & APPLIED
                        </span>
                      ) : comment.status === 'applied' ? (
                        <span className="text-[#2E7D32] bg-[#E8F5E9] px-2.5 py-0.5 rounded font-bold text-[10px] flex items-center gap-1 font-mono">
                          <Check className="w-3.5 h-3.5" /> APPLIED
                        </span>
                      ) : comment.status === 'dismissed' ? (
                        <span className="text-[#757575] bg-[#EEEEEE] px-2.5 py-0.5 rounded font-bold text-[10px] flex items-center gap-1 font-mono">
                          <X className="w-3.5 h-3.5" /> DISMISSED
                        </span>
                      ) : (
                        <span className="text-[#E65100] bg-[#FFF3E0] px-2.5 py-0.5 rounded font-bold text-[10px] flex items-center gap-1 font-mono">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-ping" /> PENDING PATCH
                        </span>
                      )}
                    </div>

                    {/* Explanation */}
                    <div className="p-5 space-y-4 bg-white">
                      <div className="text-xs text-[#191919]">
                        <Markdown>{comment.comment}</Markdown>
                      </div>

                      {/* Code comparison box */}
                      {comment.suggestion && (
                        <div className="space-y-2" id={`comparison-box-${comment.id}`}>
                          <div className="text-[10px] font-bold font-mono text-[#91918E] uppercase tracking-wider">Suggested Patch</div>
                          <div className="border border-[#EDEDEB] rounded-lg overflow-hidden font-mono text-xs">
                            {/* Old block */}
                            {comment.originalCode && (
                              <div className="bg-[#FFF5F5] border-b border-[#FFE0E0] p-4">
                                <div className="text-[10px] text-[#C62828] mb-1.5 uppercase font-bold">{"<<<< CURRENT CODE"}</div>
                                <pre className="text-[#C62828] whitespace-pre-wrap leading-relaxed font-mono">{comment.originalCode}</pre>
                              </div>
                            )}
                            {/* New block */}
                            <div className="bg-[#F1F8E9] p-4">
                              <div className="text-[10px] text-[#2E7D32] mb-1.5 uppercase font-bold">==== PROPOSED CORRECTION</div>
                              <pre className="text-[#2E7D32] whitespace-pre-wrap leading-relaxed font-mono">{comment.suggestion}</pre>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Action trigger form */}
                      {/* Action bar only for non-terminal comments — resolved,
                          applied, and dismissed are final, so a decided card is
                          read-only (no silent applied→resolved downgrade). */}
                      {comment.status !== 'resolved' && comment.status !== 'applied' && comment.status !== 'dismissed' && (
                        <div className="border-t border-[#EDEDEB] pt-4 flex flex-col sm:flex-row items-center justify-between gap-4" id={`comment-actions-${comment.id}`}>
                          <div className="w-full sm:flex-none flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                            <button
                              onClick={() => handleInteract(comment.id, 'accept')}
                              disabled={loading}
                              title="Mark this suggestion as accepted (resolved)"
                              className="w-full sm:w-auto bg-[#191919] hover:bg-[#2A2A28] disabled:opacity-50 text-white px-4 py-2 rounded-lg text-xs font-semibold transition duration-100"
                            >
                              Accept
                            </button>

                            <button
                              onClick={() => handleInteract(comment.id, 'apply')}
                              disabled={loading}
                              title="Mark this suggestion as applied (patch done)"
                              className="w-full sm:w-auto bg-[#E8F5E9] hover:bg-[#C8E6C9] text-[#2E7D32] border border-[#C8E6C9] px-4 py-2 rounded-lg text-xs font-semibold transition duration-100"
                            >
                              Applied
                            </button>

                            <button
                              onClick={() => handleInteract(comment.id, 'dismiss')}
                              disabled={loading}
                              title="Mark as won't-fix (not applied)"
                              className="w-full sm:w-auto bg-[#F7F7F5] hover:bg-[#EEEEEE] text-[#191919] border border-[#EDEDEB] px-4 py-2 rounded-lg text-xs font-semibold transition duration-100"
                            >
                              Dismiss
                            </button>
                          </div>

                          {/* Refine inputs */}
                          <div className="w-full sm:flex-1 flex gap-2">
                            <input 
                              type="text"
                              placeholder="Suggest a refinement... (e.g. 'Use parameterized variables')"
                              value={refineInputs[comment.id] || ''}
                              onChange={e => setRefineInputs(prev => ({ ...prev, [comment.id]: e.target.value }))}
                              className="flex-1 bg-white border border-[#EDEDEB] focus:border-[#191919] text-[#191919] px-3.5 py-2 text-xs rounded-lg outline-none"
                            />
                            <button
                              onClick={() => handleRefineSubmit(comment.id)}
                              disabled={loading}
                              className="bg-[#F7F7F5] hover:bg-[#F1F1EF] text-[#191919] border border-[#EDEDEB] px-3 py-2 rounded-lg text-xs"
                              title="Refine Suggestion"
                            >
                              <Send className="w-3.5 h-3.5 text-[#191919]" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* RAW REPORT PANEL: Raw metadata/payload details */}
        {activeTab === 'raw_report' && (
          <div className="space-y-6" id="panel-raw-report">
            <div className="bg-[#F7F7F5] border border-[#EDEDEB] rounded-xl p-6 space-y-4 shadow-sm" id="raw-report-summary">
              <div className="flex items-center justify-between border-b border-[#EDEDEB] pb-3">
                <h3 className="font-sans font-bold text-[#191919] text-sm flex items-center gap-2">
                  <FileCode className="w-4 h-4 text-emerald-600" />
                  Raw Execution Report Payload (JSON)
                </h3>
                <span className="font-mono text-[10px] text-[#91918E]">
                  application/json
                </span>
              </div>
              <p className="text-xs text-[#91918E] leading-relaxed">
                This panel displays the raw JSON output and log streams from the Nim automatic analysis feedback loop.
              </p>
              
              {!report ? (
                <div className="flex flex-col items-center justify-center p-12 text-[#91918E] bg-white border border-[#EDEDEB] rounded-xl font-mono text-xs">
                  No execution reports have been compiled yet. Click "Launch Nim Agent Review" to run.
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <span className="text-[10px] font-bold font-mono text-[#91918E] uppercase tracking-wider block mb-1">
                      Report Metadata
                    </span>
                    <div className="bg-white p-4 border border-[#EDEDEB] rounded-lg text-xs font-mono space-y-1">
                      <div><strong className="text-[#191919]">Report ID:</strong> {report.id}</div>
                      <div><strong className="text-[#191919]">Pull Request ID:</strong> {report.prId}</div>
                      <div><strong className="text-[#191919]">Quality Assessment Score:</strong> {report.score}/100</div>
                      <div><strong className="text-[#191919]">Automated Conclusion:</strong> {report.conclusion.toUpperCase()}</div>
                      <div><strong className="text-[#191919]">Timestamp:</strong> {new Date(report.timestamp).toLocaleString()}</div>
                    </div>
                  </div>

                  <div>
                    <span className="text-[10px] font-bold font-mono text-[#91918E] uppercase tracking-wider block mb-1">
                      Raw JSON Output
                    </span>
                    <pre className="bg-[#191919] text-emerald-400 p-5 rounded-xl border border-[#2D2D2A] text-xs font-mono whitespace-pre-wrap overflow-x-auto leading-relaxed shadow-inner max-h-[600px]">
                      {JSON.stringify(report, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
